import React, { ChangeEvent, useState, useMemo } from "react";
import "./CatalogViewerItem.scss";
import Modal from "react-modal";
import { FormattedMessage } from "react-intl";
import ICatalog_Item from "@models/ICatalog_Item";
import IImageSizeEnum from "@models/IImageSizeEnum";
import * as _ from "lodash-es";
import IOrder from "@models/IOrder";
import ISupplierModel from "@models/ISupplierModel";
import IUserUI from "@models/IUserUI";
import IVendorModel from "@models/IVendorModel";
import CatalogItemService from "@services/CatalogItemService";
import OrderEntryService from "@services/OrderEntryService";
import SupplierService from "@services/SupplierService";
import UIService from "@services/UIService";
import UserService from "@services/UserService";
import OwButton from "@components/shared/OwButton/OwButton";
import OwToggle from "@components/shared/OwToggle/OwToggle";
import OwInputWrapper from "@components/shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwS3ImageViewer from "@components/shared/OwS3ImageViewer/OwS3ImageViewer";
import { PackQtyViewer } from "@components/shared/PackQtyViewer/PackQtyViewer";
import { ItemInventoryByWarehouseModal } from "../ItemInventoryByWarehouseModal/ItemInventoryByWarehouseModal";
import IWarehouse from "@models/IWarehouse";
import IErrorDTO from "@models/DTO/IErrorDTO";
import { Socket } from "socket.io-client";
import { PricePerUnit } from "@components/shared/PricePerUnit/PricePerUnit";
import { QtyOnHandValue } from "@components/shared/QtyOnHandValue/QtyOnHandValue";
import { UomSetSelectOption } from "@components/shared/UomSetSelectOption/UomSetSelectOption";
import IUnit_Of_Measurement_Set from "@models/IUnit_Of_Measurement_Set";
import { DescriptionExpander } from "@components/shared/DescriptionExpander/DescriptionExpander";
import { FilesDownloadSection } from "@components/shared/FilesDownloadSection/FilesDownloadSection";
import OwPill from "@components/shared/OwPill/OwPill";
import { PriceBandPriceDisplay } from "@components/shared/PriceBandPriceDisplay/PriceBandPriceDisplay";
import { OrderItemOptions } from "@components/shared/OrderItemOptions/OrderItemOptions";
import { IAddBulkItems } from "@models/IAddBulkItems";
import INewOrder_Item_Option from "@models/INewOrder_Item_Option";
import { ProductGroupSelector } from "@components/shared/ProductGroupSelector/ProductGroupSelector";
import { DimensionWidget } from "@components/shared/DimensionWidget/DimensionWidget";

type Props = {
    catalogItem: ICatalog_Item;
    index: number;
    addToOrder: (items: IAddBulkItems[]) => void;
    order: IOrder;
    socket: Socket;
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    vendorModel: IVendorModel;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
    warehouses: IWarehouse[];
    hasEditableDimensions: {
        width: boolean;
        height: boolean;
        depth: boolean;
    };
};

const CatalogViewerItem: React.FC<Props> = ({
    catalogItem,
    index,
    addToOrder: addToOrderProp,
    order,
    socket,
    userModel,
    supplierModel,
    vendorModel,
    notifyFn,
    warehouses,
    hasEditableDimensions,
}) => {
    const [qty, setQty] = useState<number>(0);
    const [showQtyOnHand, setShowQtyOnHand] = useState<boolean>(false);
    const [existingQtyOnHand, setExistingQtyOnHand] = useState<number>(0);
    const [enableAdd, setEnableAdd] = useState<boolean>(false);
    const [modalComponent, setModalComponent] = useState<any>(null);
    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
    const [selectedUnit, setSelectedUnit] = useState<IUnit_Of_Measurement_Set | undefined>(catalogItem.Base_Unit);
    const [selectedItemOptions, setSelectedItemOptions] = useState<Partial<INewOrder_Item_Option>[]>([]);
    const [depth, setDepth] = useState<number>(0);
    const [isCredit, setIsCredit] = useState<boolean>(false);

    const qtyOnHandMapItem = useMemo(
        () =>
            order.Shipping_Warehouse ? CatalogItemService.getQtyOnHandWarehouseMapItem(catalogItem, order.Shipping_Warehouse) : undefined,
        [catalogItem, order.Shipping_Warehouse]
    );

    const addBulkToOrder = (items: IAddBulkItems[]) => {
        addToOrderProp(items);
    };

    // Required item options must have a value before the item can be added.
    const requiredItemOptionsComplete = CatalogItemService.areRequiredItemOptionsSelected(catalogItem, selectedItemOptions).isComplete;

    const addToOrder = () => {
        if (!requiredItemOptionsComplete) {
            return;
        }

        const itemToAdd: IAddBulkItems = {
            item: {
                catalog_item_id: catalogItem.id,
                item_no: 0,
                qty: 0,
                Catalog_Item: catalogItem,
            },
            kitDetails: [],
        };

        if (qty) {
            itemToAdd.item.qty = catalogItem.has_pack_qty ? CatalogItemService.getPackItemOrderQty(catalogItem, qty) : qty;
        }

        if (depth) {
            itemToAdd.item.depth = depth;
        }

        if (supplierModel.has_discounts) {
            const priceDetail = OrderEntryService.getDiscountedPriceForCatalogItem(catalogItem, supplierModel, order);

            itemToAdd.item.discount_id = priceDetail.discount?.id;
        }

        if (selectedUnit) {
            itemToAdd.item.uomSet = selectedUnit;
        }

        if (isCredit) {
            itemToAdd.item.is_credit = true;
        }

        if (selectedItemOptions.length) {
            itemToAdd.itemOptionValues = selectedItemOptions.map((option) => ({
                item_option_id: option.item_option_id!,
                value: option.value,
            }));
        }

        addToOrderProp([itemToAdd]);
    };

    const getToOrderQty = (item: ICatalog_Item, value: number): number => {
        let existingOrderQtyOnHand = 0;

        if (order.Order_Items) {
            for (const orderItem of order.Order_Items) {
                if (orderItem.Catalog_Item?.guid === item.guid) {
                    existingOrderQtyOnHand += Number(orderItem.qty);
                }
            }
        }

        const toOrderTotal = Number(value) + existingOrderQtyOnHand;

        return toOrderTotal;
    };

    const validateMinOrderQty = (item: ICatalog_Item, value: number): boolean => {
        let isQtyValid = true;

        const toOrderTotal = getToOrderQty(item, value);

        if (catalogItem.min_order_qty) {
            if (toOrderTotal >= catalogItem.min_order_qty) {
                isQtyValid = true;
            } else {
                isQtyValid = false;
            }
        }

        return isQtyValid;
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.type === "checked" ? e.target.checked : e.target.value;
        const name = e.target.name;

        const { enableAdd: newEnableAdd, qty: newQty } = OrderEntryService.doInventoryValidation(
            catalogItem,
            Number(value),
            order.Order_Items,
            supplierModel,
            order,
            0,
            selectedUnit
        );

        setEnableAdd(newEnableAdd);
        setQty(newQty);
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setModalComponent(null);
    };

    const viewWarehouseModal = () => {
        const modalComp = <ItemInventoryByWarehouseModal closeModalFn={closeModal} catalogItem={catalogItem!} />;

        setModalComponent(modalComp);
        setModalIsOpen(true);
    };

    const handleSelectedUnit = (set: IUnit_Of_Measurement_Set) => {
        setSelectedUnit(set);

        // do inventory validation.
        const { enableAdd: newEnableAdd, qty: newQty } = OrderEntryService.doInventoryValidation(
            catalogItem,
            qty,
            order.Order_Items,
            supplierModel,
            order,
            0,
            set
        );

        setEnableAdd(newEnableAdd);
        setQty(newQty);
    };

    let className = "odd";

    if (index % 2) {
        className = "even";
    }

    const selectedItemOptionsFn = (options: Partial<INewOrder_Item_Option>[]) => {
        setSelectedItemOptions(options);
    };

    const price_group = _.get(order, "Price_Group.value", "");
    const name = _.get(catalogItem, "name", "");
    const sku = _.get(catalogItem, "sku", "");
    const upc = _.get(catalogItem, "upc", "");
    const description = _.get(catalogItem, "description", "");
    const priceDetail = OrderEntryService.getDiscountedPriceForCatalogItem(catalogItem, supplierModel, order);
    const pricing = priceDetail.discountedPrice;
    const unit_of_measure = _.get(catalogItem, "Unit_Of_Measurement.name", "");
    const active = _.get(catalogItem, "active", true);

    let currency = order.Catalog?.Currency ? order.Catalog.Currency : undefined;

    if (order.Currency) {
        currency = order.Currency;
    }

    const prefix = currency ? OrderEntryService.getCurrencySymbol(currency.name) : "$";

    let outOfStock = false;

    if (!_.isNil(catalogItem)) {
        const { has_inventory, qty_on_hand, allow_backorder } = catalogItem;

        if (has_inventory && !_.isNil(qty_on_hand) && !qty_on_hand && !_.isNil(allow_backorder) && !allow_backorder) {
            outOfStock = true;
        }
    }

    let is_product_group = false;

    if (catalogItem && catalogItem.is_product_group) {
        is_product_group = true;
    }

    let step = SupplierService.getStep(supplierModel);

    if (catalogItem.unit_incr_qty) {
        step = catalogItem.unit_incr_qty;
    }

    let tableClass = "table-dark";

    if (UIService.isMainViewLight()) {
        tableClass = "";
    }

    const showInventoryDetails = UserService.showInventoryDetails(supplierModel, userModel);

    const deliveryEtaDetail =
        supplierModel.has_catalog_item_delivery_eta && catalogItem.delivery_eta ? (
            <tr className={"mt-2"}>
                <th>
                    <OwLabel title={"delivery_eta"} />
                </th>
                <td>
                    <div>{UserService.parseDate(catalogItem.delivery_eta)}</div>
                </td>
            </tr>
        ) : null;

    const hidePricing = UserService.hidePricing(supplierModel, userModel, vendorModel);

    const uomSetQtyConversion = OrderEntryService.getUomSetQty(qty || 1, catalogItem?.Base_Unit, selectedUnit);

    return (
        <div className={"CatalogViewerItem d-flex justify-content-center " + className}>
            <div className={"col-12 col-md-4"}>
                <div className={"item-image"}>
                    <OwS3ImageViewer
                        catalogItem={catalogItem}
                        imageIndex={IImageSizeEnum.THUMB}
                        isMicroView={false}
                        includeAllImages={true}
                        className={"mb-2 d-flex no-border"}
                    />
                </div>
                <div className={"text-center mt-2"}>
                    {catalogItem.is_kit_builder ? (
                        <div>
                            <OwLabel title={"please_use_main_item_lookup"} />
                        </div>
                    ) : null}
                    {!catalogItem.is_product_group && !catalogItem.is_kit_builder ? (
                        <div>
                            <div className={"mb-1"}>
                                <OwInputWrapper
                                    name={"qty"}
                                    fn={handleChange}
                                    value={qty}
                                    type={"number"}
                                    step={step}
                                    min={supplierModel.allow_neg_order_qty ? undefined : 0}
                                    showStep={true}
                                    disableKeyUp={true}
                                    onWheelFn={UIService.stopScrollWheel}
                                />
                            </div>
                            {hasEditableDimensions.depth &&
                                (supplierModel.show_dimension_widget && userModel.isSupplierUser ? (
                                    <DimensionWidget
                                        type="depth"
                                        fn={(value: number) => {
                                            setDepth(value);
                                        }}
                                        compact={true}
                                        value={depth}
                                        units={"imperial"}
                                        catalogViewer={true}
                                        targetUnitId={catalogItem?.dimension_unit_id}
                                    />
                                ) : null)}

                            <div>
                                {catalogItem.min_order_qty ? (
                                    <div className={"text-danger text-small"}>
                                        <OwLabel title={"min_order_qty"} postText={":"} />
                                        {catalogItem.min_order_qty}
                                    </div>
                                ) : null}
                            </div>

                            {catalogItem.has_pack_qty ? (
                                <div>
                                    <PackQtyViewer catalogItem={catalogItem} qty={qty} />
                                </div>
                            ) : null}

                            {supplierModel.has_uom_sets && catalogItem.Base_Unit ? (
                                <div className={"mt-2 mb-2"}>
                                    <UomSetSelectOption
                                        item={catalogItem}
                                        selectedOption={handleSelectedUnit}
                                        currentOption={selectedUnit}
                                    />
                                    {uomSetQtyConversion ? (
                                        <div className={"mt-2"}>
                                            {uomSetQtyConversion} {catalogItem?.Base_Unit?.unit_abbr}
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}

                            {catalogItem.Options && (
                                <div className="mt-2 mb-2">
                                    <OrderItemOptions
                                        flexDirection="flex-column"
                                        catalogItem={catalogItem}
                                        selectedItemOptionsFn={selectedItemOptionsFn}
                                    />
                                </div>
                            )}

                            {userModel.isSupplierUser && supplierModel.has_order_credits ? (
                                <div className={"mb-2 d-flex justify-content-center"}>
                                    <OwToggle
                                        label={"credit"}
                                        name={"is_credit"}
                                        active={isCredit}
                                        fn={() => setIsCredit(!isCredit)}
                                        showDisableConfirm={false}
                                    />
                                </div>
                            ) : null}

                            <OwButton
                                disabled={!active || !enableAdd || !requiredItemOptionsComplete}
                                fn={() => {
                                    addToOrder();
                                }}
                                title={"add"}
                                leftIconClass={"far fa-plus"}
                            />
                        </div>
                    ) : null}
                    <div className={active ? "d-none" : ""}>
                        <OwLabel className={"text-warning"} title={"inactive"} />
                    </div>

                    <div className={outOfStock ? "" : "d-none"}>
                        <OwLabel className={"text-warning"} title={"out_of_stock"} />
                    </div>

                    {catalogItem.is_ordered_on_demand ? (
                        <div className={"mt-4"}>
                            <OwPill title={"ordered_on_demand"} className={"text-small"} />
                        </div>
                    ) : null}
                </div>
            </div>
            <div className={"col-12 col-md-8"}>
                <table className={"table table-borderless " + tableClass}>
                    <tbody>
                        <tr>
                            <th>
                                <FormattedMessage id={"name"} defaultMessage={"Name"} description="Name Title" />
                            </th>
                            <td className={"d-flex"}>
                                <div>{name}</div>
                                {is_product_group ? (
                                    <div className={"ms-auto text-primary"}>
                                        <i className={"far fa-sitemap"} title={"Product Group"}></i>
                                    </div>
                                ) : null}
                            </td>
                        </tr>
                        <tr>
                            <th>
                                <FormattedMessage id={"sku"} defaultMessage={"SKU"} description="SKU Title" />
                            </th>
                            <td className={"d-flex"}>
                                <div>{sku}</div>
                            </td>
                        </tr>
                        <tr>
                            <th>
                                <FormattedMessage id={"upc"} defaultMessage={"UPC"} description="UPC Title" />
                            </th>
                            <td className={"d-flex"}>
                                <div>{upc}</div>
                            </td>
                        </tr>
                        <tr>
                            <th>
                                <OwLabel title={"description"} />
                            </th>
                            <td className={"pre-wrap"}>
                                {(catalogItem.short_description?.length || 0) > 0 ? (
                                    <div className={"mb-3"}>{catalogItem.short_description}</div>
                                ) : null}
                                <DescriptionExpander catalogItem={catalogItem} priceGroup={order?.Price_Group} />
                            </td>
                        </tr>
                        {!catalogItem.is_product_group && !hidePricing ? (
                            <>
                                {supplierModel.has_map ? (
                                    <tr>
                                        <th>
                                            <OwLabel title={"MAP"} />
                                        </th>
                                        <td>
                                            <PricePerUnit
                                                priceDetail={{
                                                    discountedPrice: catalogItem.map || 0,
                                                    originalPrice: catalogItem.map || 0,
                                                }}
                                                prefix={prefix}
                                                currency={currency}
                                                baseCurrency={order.Catalog?.Currency}
                                                exchangeRate={order.exchange_rate}
                                            />
                                        </td>
                                    </tr>
                                ) : null}
                                <tr>
                                    <th>
                                        <FormattedMessage id={"price"} defaultMessage={"Price"} description="Price Title" />
                                    </th>
                                    <td>
                                        {catalogItem.has_price_bands &&
                                        order.Price_Group &&
                                        !OrderEntryService.isSumComponentPricedKit(catalogItem) ? (
                                            <PriceBandPriceDisplay
                                                bands={(catalogItem.Price_Bands || {})[order.Price_Group.value]}
                                                currency={order.Catalog?.Currency}
                                            />
                                        ) : (
                                            <PricePerUnit
                                                priceDetail={priceDetail}
                                                prefix={prefix}
                                                currency={currency}
                                                baseCurrency={order.Catalog?.Currency}
                                                exchangeRate={order.exchange_rate}
                                            />
                                        )}
                                    </td>
                                </tr>
                            </>
                        ) : null}
                        {catalogItem.has_inventory && showInventoryDetails && !catalogItem.is_product_group ? (
                            <tr>
                                <th>
                                    <OwLabel title={"qty_on_hand"} noBottomMargin={true} />
                                </th>
                                <td>
                                    <QtyOnHandValue
                                        catalogItem={catalogItem}
                                        mapItem={qtyOnHandMapItem}
                                        shippingWarehouse={order.Shipping_Warehouse}
                                    />
                                    {catalogItem.Warehouse_Catalog_Items_Maps?.length ? (
                                        <div>
                                            <OwLabel title={"warehouses"} fn={viewWarehouseModal} />
                                        </div>
                                    ) : null}
                                </td>
                            </tr>
                        ) : null}
                        {deliveryEtaDetail}
                        <tr>
                            <th>
                                <FormattedMessage id={"unit"} defaultMessage={"Unit"} description="Unit Title" />
                            </th>
                            <td>{unit_of_measure}</td>
                        </tr>
                        {(supplierModel.Supplier_Integration.QBD_Custom_Fields || [])
                            .filter((item) => item.display_only)
                            .map((item, index) => {
                                const displayItem = (catalogItem.display_fields || []).find(
                                    (displayField) => item.name === displayField.name
                                );
                                return (
                                    <tr key={item.guid}>
                                        <th>{item.name}</th>
                                        <td>{displayItem?.value}</td>
                                    </tr>
                                );
                            })}
                    </tbody>
                </table>
                {catalogItem.is_product_group && (
                    <ProductGroupSelector
                        supplierModel={supplierModel}
                        catalogItem={catalogItem}
                        order={order}
                        notifyFn={notifyFn}
                        socket={socket}
                        warehouses={warehouses}
                        addItems={addBulkToOrder}
                        orientation="vertical"
                    />
                )}
                <FilesDownloadSection catalogItem={catalogItem} readOnly={true} />

                <div>
                    <Modal
                        isOpen={modalIsOpen}
                        contentLabel="Orderwerks"
                        shouldCloseOnOverlayClick={false}
                        className={"modalContentClass"}
                        overlayClassName={"modalOverlayClass"}>
                        {modalComponent}
                    </Modal>
                </div>
            </div>
        </div>
    );
};

export default CatalogViewerItem;
